import React, { useState, useEffect, useCallback } from 'react';
import { Paper, Box, IconButton, Tabs, Tab, Typography } from '@mui/material';
import Settings from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import GitBranch from '@mui/icons-material/AltRoute';
import { nanoid } from 'nanoid';
import NodeHeader from './NodeHeader';
import PropertiesTab from './PropertiesTab';
import RoutesTab from './RoutesTab';
import { COLORS, NODE_HEADER_COLORS, OPERATORS } from '../../constants';

/** Ensure every route and criterion has a stable keyId for list keys (satisfies react/no-array-index-key). */
function ensureRouteKeys(data) {
  if (!data?.routes) return data;
  const routes = data.routes.map(route => {
    const r = { ...route, keyId: route.keyId || nanoid() };
    if (r.criteria) {
      r.criteria = r.criteria.map(c => ({ ...c, keyId: c.keyId || nanoid() }));
    }
    return r;
  });
  return { ...data, routes };
}

function PropertiesPanel({ selectedNode, nodes, onUpdate, onClose, readOnly }) {
  const [formData, setFormData] = useState(null);
  const [activeTab, setActiveTab] = useState('properties');

  useEffect(() => {
    if (selectedNode) {
      const raw = JSON.parse(JSON.stringify(selectedNode.data.original));
      setFormData(ensureRouteKeys(raw));
    } else {
      setFormData(null);
    }
  }, [selectedNode]);

  useEffect(() => {
    if (selectedNode?.id) {
      setActiveTab('properties');
    }
  }, [selectedNode?.id]);

  const applyUpdate = useCallback(
    updated => {
      setFormData(updated);
      onUpdate({
        ...selectedNode,
        data: { ...selectedNode.data, original: updated },
      });
    },
    [selectedNode, onUpdate],
  );

  if (!selectedNode || !formData) return null;

  const handleAttributeChange = (key, value) => {
    applyUpdate({
      ...formData,
      attributes: { ...formData.attributes, [key]: value },
    });
  };

  const handleCriteriaChange = (routeIndex, criteriaIndex, field, value) => {
    const newRoutes = [...(formData.routes || [])];
    const newCriteria = [...newRoutes[routeIndex].criteria];
    newCriteria[criteriaIndex] = {
      ...newCriteria[criteriaIndex],
      [field]: value,
    };
    newRoutes[routeIndex] = { ...newRoutes[routeIndex], criteria: newCriteria };
    applyUpdate({ ...formData, routes: newRoutes });
  };

  const handleRouteGotoChange = (routeIndex, gotoValue) => {
    const newRoutes = [...(formData.routes || [])];
    newRoutes[routeIndex] = { ...newRoutes[routeIndex], goto: gotoValue };
    applyUpdate({ ...formData, routes: newRoutes });
  };

  const addRoute = () => {
    if (formData.type === 'SWITCH') {
      const matchRouteIndex = formData.routes?.findIndex(
        r => r.signal === 'MATCH',
      );
      if (matchRouteIndex >= 0) {
        const newRoutes = [...formData.routes];
        const newCriteria = [...(newRoutes[matchRouteIndex].criteria || [])];
        newCriteria.push({
          keyId: nanoid(),
          operator: OPERATORS.EQUAL,
          value: '',
          goto: null,
        });
        newRoutes[matchRouteIndex] = {
          ...newRoutes[matchRouteIndex],
          criteria: newCriteria,
        };
        applyUpdate({ ...formData, routes: newRoutes });
      } else {
        const newCriteriaRoute = {
          keyId: nanoid(),
          signal: 'MATCH',
          criteria: [
            {
              keyId: nanoid(),
              operator: OPERATORS.EQUAL,
              value: '',
              goto: null,
            },
          ],
          goto: null,
        };
        applyUpdate({
          ...formData,
          routes: [...(formData.routes || []), newCriteriaRoute],
        });
      }
    } else {
      const newRoute = { keyId: nanoid(), goto: null, signal: 'NEXT' };
      applyUpdate({
        ...formData,
        routes: [...(formData.routes || []), newRoute],
      });
    }
  };

  const removeCriteria = (routeIndex, criteriaIndex) => {
    const routes = formData.routes ?? [];
    if (routeIndex < 0 || routeIndex >= routes.length) return;
    const route = routes[routeIndex];
    const criteria = route?.criteria ?? [];
    if (criteriaIndex < 0 || criteriaIndex >= criteria.length) return;
    const newRoutes = [...routes];
    const newCriteria = [...criteria];
    newCriteria.splice(criteriaIndex, 1);
    newRoutes[routeIndex] = { ...route, criteria: newCriteria };
    applyUpdate({ ...formData, routes: newRoutes });
  };

  const removeRoute = index => {
    const routes = formData.routes ?? [];
    if (index < 0 || index >= routes.length) return;
    const newRoutes = [...routes];
    newRoutes.splice(index, 1);
    applyUpdate({ ...formData, routes: newRoutes });
  };

  const headerColor =
    NODE_HEADER_COLORS[formData.type] ?? COLORS.HEADER_DEFAULT;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        right: 16,
        top: 16,
        bottom: 16,
        width: 400,
        maxWidth: 'min(400px, calc(100vw - 32px))',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 1001,
        borderRadius: 2,
      }}>
      <Box
        sx={{
          bgcolor: headerColor,
          p: 2,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}>
        <NodeHeader type={formData.type} id={formData.id} />
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: 'rgba(255,255,255,0.8)',
            '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.2)' },
          }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          variant="fullWidth">
          <Tab
            value="properties"
            label={
              <Box display="flex" gap={1} alignItems="center">
                <Settings sx={{ fontSize: 16 }} /> Properties
              </Box>
            }
          />
          <Tab
            value="routes"
            label={
              <Box display="flex" gap={1} alignItems="center">
                <GitBranch sx={{ fontSize: 16 }} /> Routes{' '}
                <Box
                  component="span"
                  sx={{
                    bgcolor: 'action.hover',
                    px: 1,
                    borderRadius: 10,
                    fontSize: 10,
                  }}>
                  {formData.routes?.length || 0}
                </Box>
              </Box>
            }
          />
        </Tabs>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, bgcolor: 'grey.50' }}>
        {activeTab === 'properties' && (
          <>
            {formData.type === 'TRIGGER' && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Flow Name: {formData.flowName}
                </Typography>
              </Box>
            )}
            <PropertiesTab
              attributes={formData.attributes}
              onAttributeChange={handleAttributeChange}
              nodeType={formData.type}
              routes={formData.routes}
              readOnly={readOnly}
            />
          </>
        )}
        {activeTab === 'routes' && (
          <RoutesTab
            routes={formData.routes}
            nodes={nodes}
            type={formData.type}
            selectedNodeId={selectedNode.id}
            onAddRoute={formData.type === 'SWITCH' ? addRoute : undefined}
            onRemoveRoute={formData.type === 'SWITCH' ? removeRoute : undefined}
            onCriteriaChange={handleCriteriaChange}
            onRemoveCriteria={removeCriteria}
            onRouteGotoChange={handleRouteGotoChange}
            readOnly={readOnly}
          />
        )}
      </Box>
      {!readOnly && (
        <Box
          sx={{
            p: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            textAlign: 'center',
          }}>
          <Typography variant="caption" color="text.disabled">
            Edits apply to the canvas; click Save in the toolbar to persist.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default PropertiesPanel;
